/**
 * Public "my tickets" contracts (plan 2026-07-31-002).
 *
 * One signed-in mobile user opens a link to a public web page listing the
 * upcoming events they hold a spot at — paid tickets and free sign-ups alike —
 * at one school. Ownership is not asserted by the page: the link carries a
 * `UsersOnSchools` publicId, and the backend resolves that to the platform
 * user's email and matches it against `buyerEmail` scoped to the school in the
 * link (KTD1, KTD2, R14).
 *
 * The link is a bearer credential (R15) — whoever holds it reads that user's
 * orders and it does not expire — so the params below are the entire access
 * surface. There is no body and no mutation shape here on purpose.
 *
 * The rows reuse `TicketedEventOrderSummarySchema` rather than declaring a
 * parallel order DTO (KTD7). Everything the page renders — event, start,
 * location, quantity held — is on that shape already. Rows deliberately carry
 * no payment state (KTD12); the confirmation page each row links to surfaces
 * it.
 *
 * Since the ticketed-events admission restructure, that row also carries an
 * optional `admissions[]` — one entry per seat held, so the page can render a
 * per-seat ticket instead of a bare quantity. It is optional and additive: the
 * declaration ships ahead of the backend that emits it, so an installed mobile
 * build parses the restructured payload with no change (SCH-1622, R46/AE15).
 */

import { z } from 'zod'

import { TicketedEventOrderSummarySchema } from '../../schemas/entities/ticketed-event-order'

/**
 * Path params for GET /v1/ticketed-events/by-slug/:schoolSlug/my-tickets/:schoolUserId.
 *
 * `schoolUserId` is a `UsersOnSchools` publicId (a CUID), not a platform user
 * id — the pair is what scopes the read to one person at one school.
 */
export const PublicMyTicketsParamsSchema = z.object({
  schoolSlug: z.string().min(1),
  schoolUserId: z.string().min(1)
})
export type PublicMyTicketsParams = z.infer<typeof PublicMyTicketsParamsSchema>

/**
 * Path params for GET /v1/ticketed-events/by-slug/:schoolSlug/my-tickets/t/:token
 * (SCH-3420). `token` is the short-lived signed link a signed-in user mints with
 * POST /v1/ticketed-events/my-tickets/link; it names the school-user and the
 * school slug and expires 15 minutes after minting. Unlike the CUID route above,
 * the token is not visible anywhere else, and it stops working on its own.
 */
export const PublicMyTicketsTokenParamsSchema = z.object({
  schoolSlug: z.string().min(1),
  token: z.string().min(1)
})
export type PublicMyTicketsTokenParams = z.infer<typeof PublicMyTicketsTokenParamsSchema>

/**
 * The page payload: upcoming orders soonest-first (R7), each already filtered
 * server-side to confirmed + awaiting-payment on non-application events
 * (KTD6, R4, R6, R16).
 *
 * `meta.total` is the number of rows returned, not a page count — this read is
 * unpaginated. It exists so the page can distinguish a genuinely-empty result
 * from a failed fetch without inferring emptiness from an empty array (KTD9,
 * R9).
 */
export const PublicMyTicketsResponseSchema = z.object({
  data: z.array(TicketedEventOrderSummarySchema),
  meta: z.object({
    total: z.number().int(),
    /**
     * The SCHOOL's IANA zone, not the viewer's. Every row belongs to the one
     * school in the link, so it rides on `meta` rather than repeating per row.
     *
     * Load-bearing: these times must render the same here, on the events tab,
     * and on the confirmation page each row links to. A parent in Denver
     * reading a New York school's page has to see the New York time or they
     * arrive two hours off — and the day itself can flip, since the backend
     * decides "upcoming" against the school's day. Null when the school has no
     * zone on file; the formatter falls back to UTC.
     */
    schoolTimeZone: z.string().nullable()
  })
})
export type PublicMyTicketsResponse = z.infer<typeof PublicMyTicketsResponseSchema>

/**
 * GET /v1/ticketed-events/my-tickets (SCH-3420): the signed-in caller's own
 * upcoming orders at the active school (`x-school-id`). No params: the session
 * names the person, so no ID in the request can point it at someone else. Same
 * payload as the public read above.
 */
export const MyTicketsResponseSchema = PublicMyTicketsResponseSchema
export type MyTicketsResponse = PublicMyTicketsResponse

/**
 * POST /v1/ticketed-events/my-tickets/link (SCH-3420): a signed web link to the
 * caller's my-tickets page at the active school, for the hand-off into a
 * browser. No body. `url` carries a bearer token good for 15 minutes; `expiresAt`
 * is when it stops working (ISO 8601).
 */
export const MyTicketsLinkSchema = z.object({
  url: z.string(),
  expiresAt: z.string()
})
export type MyTicketsLink = z.infer<typeof MyTicketsLinkSchema>

export const MyTicketsLinkResponseSchema = z.object({ data: MyTicketsLinkSchema })
export type MyTicketsLinkResponse = z.infer<typeof MyTicketsLinkResponseSchema>
